/**
 * Voice Context
 *
 * Global state management for voice assistant sessions.
 * Provides voice session state, controls, and settings across the app.
 *
 * @module contexts/VoiceContext
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  useGeminiLiveSession,
  type VoiceSessionState,
  type GeminiVoice,
  type TranscriptEntry,
  type ToolCallInfo,
} from '../hooks/useGeminiLiveSession';

// Re-export types for consumers
export type { GeminiVoice, VoiceSessionState, TranscriptEntry, ToolCallInfo };

// =============================================================================
// Types
// =============================================================================

export interface VoiceSettings {
  voice: GeminiVoice;
  autoConnect: boolean;
  showTranscript: boolean;
}

export interface VoiceContextValue {
  // Session state
  state: VoiceSessionState;
  sessionId: string | null;
  transcript: TranscriptEntry[];
  toolCalls: ToolCallInfo[];
  error: string | null;
  isRecording: boolean;

  // Settings
  settings: VoiceSettings;
  updateSettings: (updates: Partial<VoiceSettings>) => void;

  // Actions
  connect: () => Promise<void>;
  disconnect: () => void;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  toggleRecording: () => Promise<void>;
  sendText: (text: string) => void;
  clearTranscript: () => void;

  // Computed
  isConnected: boolean;
  isActive: boolean;
  canRecord: boolean;
}

// =============================================================================
// Context
// =============================================================================

const VoiceContext = createContext<VoiceContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

const DEFAULT_SETTINGS: VoiceSettings = {
  voice: 'Kore',
  autoConnect: false,
  showTranscript: true,
};

export function VoiceProvider({ children }: { children: React.ReactNode }) {
  // Voice session hook
  const {
    state,
    sessionId,
    transcript,
    toolCalls,
    error,
    isRecording,
    connect: connectSession,
    disconnect: disconnectSession,
    startRecording: startRec,
    stopRecording: stopRec,
    sendText,
    clearTranscript,
  } = useGeminiLiveSession();

  // Settings state (persisted to localStorage)
  const [settings, setSettings] = useState<VoiceSettings>(() => {
    try {
      const saved = localStorage.getItem('voice-settings');
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  // Update settings
  const updateSettings = useCallback((updates: Partial<VoiceSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...updates };
      localStorage.setItem('voice-settings', JSON.stringify(next));
      return next;
    });
  }, []);

  // Connect with current settings
  const connect = useCallback(async () => {
    await connectSession({ voice: settings.voice });
  }, [connectSession, settings.voice]);

  // Disconnect
  const disconnect = useCallback(() => {
    disconnectSession();
  }, [disconnectSession]);

  // Start recording
  const startRecording = useCallback(async () => {
    // Auto-connect if not connected
    if (state === 'idle' || state === 'error') {
      await connect();
      // connect() now properly waits for session to be ready
    }
    // Start recording - the hook will handle state validation
    try {
      await startRec();
    } catch (err) {
      console.error('[VoiceContext] Failed to start recording:', err);
    }
  }, [state, connect, startRec]);

  // Stop recording
  const stopRecording = useCallback(() => {
    stopRec();
  }, [stopRec]);

  // Toggle recording
  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      stopRecording();
    } else {
      await startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Computed values
  const isConnected = state === 'connected' || state === 'listening' || state === 'processing' || state === 'speaking';
  const isActive = state !== 'idle' && state !== 'error';
  const canRecord = isConnected && !isRecording;

  // Context value
  const value = useMemo<VoiceContextValue>(
    () => ({
      state,
      sessionId,
      transcript,
      toolCalls,
      error,
      isRecording,
      settings,
      updateSettings,
      connect,
      disconnect,
      startRecording,
      stopRecording,
      toggleRecording,
      sendText,
      clearTranscript,
      isConnected,
      isActive,
      canRecord,
    }),
    [
      state,
      sessionId,
      transcript,
      toolCalls,
      error,
      isRecording,
      settings,
      updateSettings,
      connect,
      disconnect,
      startRecording,
      stopRecording,
      toggleRecording,
      sendText,
      clearTranscript,
      isConnected,
      isActive,
      canRecord,
    ]
  );

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

export function useVoice(): VoiceContextValue {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
}

export default VoiceContext;
